import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import instance from "../network";
import { fetchCsrfCookie } from "@/utils/csrf";
import {
  authenticate,
  getCookie,
  removeCookie,
  setCookie,
  logout as clearStorage,
} from "@/network/helper";
import { handleToast } from "../network/helper";
import { setBrand, toggleIsTourGuideFinished } from "@/store/common";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "@/store/loader";
import { generateMaaranToken, clearMaaranToken } from "@/utils/easygtm";

export const useAuth = () => {
  const router = useRouter();
  const dispatch = useDispatch();

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [plan, setPlan] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Centralized error handler
  const handleError = (err, defaultMessage) => {
    const message =
      err.response?.data?.message || err.message || defaultMessage;
    handleToast({ err: { response: { data: { message } } } });
  };

  // Fetch CSRF token
  const csrf = async () => {
    try {
      await fetchCsrfCookie();
    } catch (err) {
      handleError(err, "Failed to fetch CSRF token.");
    }
  };

  // Fetch user data
  const fetchUser = async () => {
    try {
      const res = await instance.get("/api/user");

      if (res?.status === 200 && res?.data?.data) {
        const userData = res.data.data;

        setUser(userData);
        setCookie("user", JSON.stringify(userData));
        setIsAuthenticated(true);

        await authenticate(userData); // Removed unnecessary empty callback
        await fetchOrganization(); // Fetch org only if user is valid

        return userData; // ✅ Ensures user data is always returned on success
      }
    } catch (err) {
      handleError(err, "Failed to fetch user data.");
      setIsAuthenticated(false);
      setUser(null);
    }
    return null; // ✅ Always returns a value, even in case of an error
  };

  // Fetch organization data
  const fetchOrganization = async () => {
    try {
      const res = await instance.get("/api/v1/organization");
      const organizationData = res?.data?.data;

      if (organizationData?.id) {
        setCookie("inviteModalIsOpened", "true");
        setCookie("organization", JSON.stringify(organizationData));
        setCookie("isLoggedInYN", "true");
        const storedUser = getCookie("user");
        const userData = storedUser ? JSON.parse(storedUser) : null;

        if (
          userData?.attributes?.role_numeric === 1 ||
          userData?.attributes?.role_numeric === 2
        ) {
          await fetchSubscriptionDetails();
        }

        setIsAuthenticated(true);
      } else {
        setCookie("inviteModalIsOpened", "false");
        removeCookie("organization");
        setCookie("isLoggedInYN", "true");
        router.push("/organization"); // Ensuring navigation completes before returning
      }

      return res?.data?.data; // ✅ Returning response after all conditions are executed
    } catch (err) {
      handleError(err, "Failed to fetch organization data.");
      setIsAuthenticated(false);
      setUser(null);
      return null; // ✅ Ensuring a return value even in case of failure
    }
  };

  // Function to create an organization
  const createOrganization = async (data) => {
    try {
      const response = await instance.post("/api/v1/organization", data);
      // If the response is successful, store the organization data in a cookie
      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 204
      ) {
        const organizationData = response?.data?.data;
        if (organizationData?.id) {
          setCookie("organization", JSON.stringify(organizationData));
          setCookie("isLoggedInYN", "true");
        }
        return response?.data;
      }
      return response?.data;
    } catch (error) {
      console.log(error,'error')
      // handleError(error, "Something went wrong");
      // Log and rethrow error for further handling
    }
  };
  // Function to create an organization
  const updateOrganization = async (data) => {
    try {
      const response = await instance.patch("/api/v1/organization", data);
      // If the response is successful, store the organization data in a cookie
      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 204
      ) {
        const organizationData = response?.data?.data;
        if (organizationData?.id) {
          setCookie("organization", JSON.stringify(organizationData));
        }
        return response?.data;
      }
      return response?.data;
    } catch (error) {
      handleError(error, "Something went wrong");
      // Log and rethrow error for further handling
    }
  };

  // Login
  const login = async (data) => {
    await clearStorage();
    try {
      await csrf();
      const response = await instance.post("/login", data);
      if ([200, 201, 204].includes(response?.status)) {
        setCookie("inviteModalIsOpened", "true");
        dispatch(toggleIsTourGuideFinished(2));

        await generateMaaranToken();

        const userResp = await fetchUser();
        if (!userResp) throw new Error("User fetch failed");
        router.push("/ai-mode");
        handleToast({ res: { message: "Login successful!" } });
      }
    } catch (err) {
      console.log(err,'error')
    }
  };

  // Logout
  const logout = async () => {
    try {
      dispatch(showLoader());
      try {
        await instance.post("/logout");
        await clearStorage();

        // Clear MAARAN API token
        clearMaaranToken();

        setIsAuthenticated(false);
        setUser(null);
        setOrg(null);

        handleToast({ res: { message: "Logout successful." } });
        window.amplitude.reset();
        window.location.reload();
      } catch (logoutError) {
        // If logout API returns 401 or 419, reload immediately
        if (
          logoutError.response?.status === 401 ||
          logoutError.response?.status === 419
        ) {
          console.warn("Logout API returned 401/419, reloading...");
          window.location.reload();
        } else {
          handleError(logoutError, "Logout request failed.");
        }
      }
    } catch (err) {
      console.error("Unexpected error during logout:", err);
    } finally {
      dispatch(hideLoader());
    }
  };

  // Register
  const register = async (data) => {
    await clearStorage();
    await csrf(); // Ensure CSRF token is set
    setCookie("inviteModalIsOpened", "false");

    try {
      const res = await instance.post("/register", data);
      if ([200, 201, 204, 302]?.includes(res?.status)) {
        handleToast({
          res: { message: "Registration successful! Welcome aboard." },
        });

        dispatch(toggleIsTourGuideFinished(0));
        dispatch(setBrand(null));

        // Generate MAARAN API token for AI features
        await generateMaaranToken();

        const userResp = await fetchUser();
        if (!userResp) throw new Error("User fetch failed");

        const orgResp = await fetchOrganization();
        if (!orgResp?.id) {
          router.push("/organization");
        } else {
          let redirectPath =
            userResp?.attributes?.role_numeric === 4
              ? "/task-management"
              : "/campaigns";

          router.push(redirectPath);
        }

        removeCookie("inviteToken");
      }
    } catch (err) {
      // Pass the error to the caller function
       console.log(err) // Important
        //  handleToast({ err });
         return err;
    }
  };

  // subscription details
  const fetchSubscriptionDetails = async () => {
    try {
      const res = await instance.get("/api/v1/subscription?include=items");
      const subDetails = res?.data?.data; // Accessing the subscription object directly

      if (subDetails) {
        setSubscription(subDetails);

        // Store the subscription data as a cookie (JSON string)
        setCookie("subscription", JSON.stringify(subDetails), {
          maxAge: 60 * 60 * 24 * 30, // Cookie expires in 30 days
          path: "/", // Make it accessible across the site
          sameSite: "strict",
        });
      } else {
        setSubscription(null);
      }
    } catch (err) {
      console.error("Error:", err?.message);
    }
  };

  useEffect(() => {
    const loadUserFromCookies = () => {
      try {
        const storedUser = getCookie("user");
        const storedOrg = getCookie("organization");
        const storedPlan = getCookie("plan");
        const storedSubscription = getCookie("subscription");

        const userData = storedUser ? JSON?.parse(storedUser) : null;
        const orgData = storedOrg ? JSON.parse(storedOrg) : null;
        const planData = storedPlan ? JSON.parse(storedPlan) : null;
        const subscriptionData = storedSubscription
          ? JSON.parse(storedSubscription)
          : null;

        if (userData) {
          setUser(userData);
          setOrg(orgData);
          setPlan(planData);
          setSubscription(subscriptionData);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Error loading user from cookies:", error);
      }
    };

    loadUserFromCookies();
  }, []);

  return {
    user,
    isAuthenticated,
    login,
    logout,
    register,
    org,
    fetchOrganization,
    createOrganization,
    setUser,
    setOrg,
    updateOrganization,
    plan,
    subscription,
    fetchSubscriptionDetails,
    csrf,
    fetchUser,
  };
};
